import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * HIPAA safeguard: auto sign-out after a period of inactivity so PHI isn't
 * left exposed on an unattended device. Renders nothing.
 */
const IDLE_MINUTES = 20;
const WARN_SECONDS = 60;

export const SessionTimeout = () => {
  const { user, logout } = useAuth();
  const timerRef = useRef(null);
  const warnRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warnRef.current) clearTimeout(warnRef.current);
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    if (!user) return;
    warnRef.current = setTimeout(() => {
      toast.warning('You will be signed out soon for your security.', {
        description: 'Move your mouse or press a key to stay signed in.',
        duration: WARN_SECONDS * 1000,
      });
    }, (IDLE_MINUTES * 60 - WARN_SECONDS) * 1000);
    timerRef.current = setTimeout(() => {
      toast.info('Signed out due to inactivity to protect your information.');
      logout();
    }, IDLE_MINUTES * 60 * 1000);
  }, [user, logout, clearTimers]);

  useEffect(() => {
    if (!user) {
      clearTimers();
      return;
    }
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const onActivity = () => reset();
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearTimers();
    };
  }, [user, reset, clearTimers]);

  return null;
};

export default SessionTimeout;
